import React, { useEffect, useRef, useState } from 'react';

import {
	Checkbox,
	DynamicIconWrapper,
	Icons,
	SubmissionButtons,
	Tooltip,
} from '@components/common';
import RadioButton from '@components/common/Form/RadioButton';
import {
	accessAvailabilityOptions,
	assetsColorOptions,
} from '@components/Map/MapControls/Controls/Filters/AssetsFilters/filterItems';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	applyFilterValues,
	disableOoklaVisibility,
	resetAttributesFilter,
	resetRightSideControls,
	setAttributesFilter,
	setColorAssetsBy,
	setSiteAccessAvailability,
} from '@slices/map';

import { TSiteAccessAvailability } from '@components/Map/MapControls/Controls/Filters/AssetsFilters/types';

import { EColorAssetsBy, TColorAssetsBy } from '@enums/map';

import commonStyles from '@styles/common.module.scss';

import styles from './Tabs.module.scss';

const Attributes = () => {
	const dispatch = useAppDispatch();
	const {
		currentAssetsFilterValues: { attributes },
		siteAccessAvailable,
		colorAssetsBy,
		isApplyFiltersAvailable,
		pinnedFilter,
	} = useAppSelector(map);

	const [selectedColorAssetStatus, setSelectedColorAssetStatus] = useState(() => colorAssetsBy);
	const [isColorStatusChanges, setIsColorStatusChanges] = useState(false);
	const [isDisableApplyButton, setIsDisableApplyButton] = useState(false);

	const prevColorAssetStatus = useRef(selectedColorAssetStatus);

	const handleChangeAttributes = (attributeIdOrName: string) => dispatch(setAttributesFilter({ attributeIdOrName }));

	const handleChangeSiteAccessAvailability = (value: TSiteAccessAvailability) => {
		dispatch(setSiteAccessAvailability(value));

		setIsDisableApplyButton(false);
	};

	const handleChangeColorAssetsBy = (value: TColorAssetsBy) => {
		setSelectedColorAssetStatus(value);
		setIsDisableApplyButton(false);
	};

	const resetAttributes = () => {
		dispatch(resetAttributesFilter());
		dispatch(applyFilterValues({}));

		setSelectedColorAssetStatus(EColorAssetsBy.none);

		if (!pinnedFilter) dispatch(resetRightSideControls());
	};

	const applyFilters = () => {
		dispatch(setColorAssetsBy(selectedColorAssetStatus));
		dispatch(applyFilterValues({}));

		if (selectedColorAssetStatus !== EColorAssetsBy.none) {
			dispatch(disableOoklaVisibility());
		}

		setIsDisableApplyButton(true);

		if (!pinnedFilter) dispatch(resetRightSideControls());
	};

	useEffect(() => {
		setIsColorStatusChanges(prevColorAssetStatus.current !== selectedColorAssetStatus);
		prevColorAssetStatus.current = selectedColorAssetStatus;
	}, [selectedColorAssetStatus]);

	useEffect(() => {
		setIsDisableApplyButton(false);
	}, [attributes]);

	return (
		<div className={styles.filterContainer}>
			<div className={styles.attributesFilter}>
				<div className={styles.title}>
					Show Assets on Map
					<Tooltip
						content={
							<div>
								Filter by parameters which Assets
								<br />
								should be shown on the map
							</div>
						}>
						<span className={commonStyles.infoIcon}>
							<DynamicIconWrapper icon={Icons.info} />
						</span>
					</Tooltip>
				</div>
				{attributes.map(({ id, name, items }) => {
					return (
						<div key={id}>
							{name}
							<div className={styles.attributeCheckboxes}>
								{!!items?.length && items.map((item) => {
									return (
										<Checkbox
											key={item.id}
											checked={item.isSelected}
											color={item.color}
											label={item.name}
											name={item.id}
											onChange={() => handleChangeAttributes(item.id)}
										/>
									);
								})}
							</div>
						</div>
					);
				})}
				<div>
					Site Access Availability
					<div className={styles.radioButtons}>
						{accessAvailabilityOptions.map(option => (
							<RadioButton
								key={option}
								checked={option === siteAccessAvailable}
								id={option}
								label={option}
								name='siteAccessAvailability'
								onChange={handleChangeSiteAccessAvailability}
							/>
						))}
					</div>
				</div>
				<div className={styles.title}>
					Color Assets by Status
					<Tooltip
						content={
							<div>
								Assets will be colored according
								<br />
								to the parameters selected above
							</div>
						}
					>
					<span className={commonStyles.infoIcon}>
						<DynamicIconWrapper icon={Icons.info} />
					</span>
					</Tooltip>
				</div>
				<div className={styles.colorAssetsByRadioButtons}>
					{assetsColorOptions.map(option => (
						<RadioButton
							key={option.value}
							checked={option.value === selectedColorAssetStatus}
							id={option.value}
							label={option.label}
							name='colorAssetsBy'
							onChange={handleChangeColorAssetsBy}
						/>
					))}
				</div>
			</div>
			<SubmissionButtons
				className={styles.buttonsContainer}
				disabledPositive={(!isApplyFiltersAvailable && !isColorStatusChanges) || isDisableApplyButton}
				negativeHandler={resetAttributes}
				negativeLabel='Reset all'
				positiveHandler={applyFilters}
				positiveLabel='Apply'
			/>

		</div>
	);
};

export default Attributes;
